/**
 * Product State Handler
 * Handles button text updates based on product state and variant availability
 */

(function() {
  'use strict';

  class ProductStateHandler {
    constructor() {
      this.productStateData = null;
      this.productInfo = null;
      this.submitButton = null;
      this.submitButtonText = null;
      this.init();
    }

    init() {
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setup());
      } else {
        this.setup();
      }
    }

    setup() {
      // Get product state data from JSON script tag
      const productStateScript = document.getElementById('product-state-data');
      if (!productStateScript) {
        return;
      }

      try {
        this.productStateData = JSON.parse(productStateScript.textContent);
      } catch (e) {
        console.error('Failed to parse product state data:', e);
        return;
      }

      // Find product info element and button
      this.productInfo = document.querySelector('product-info');
      if (!this.productInfo) {
        return;
      }

      this.findSubmitButton();
      this.updateProductMessages();

      // Listen for variant changes
      this.listenForVariantChanges();
    }

    findSubmitButton() {
      this.submitButton = this.productInfo.querySelector('[type="submit"]');
      if (this.submitButton) {
        this.submitButtonText = this.submitButton.querySelector('[data-add-to-cart-text]');
      }
    }

    listenForVariantChanges() {
      // Subscribe to variant change events
      if (typeof subscribe !== 'undefined' && window.theme && window.theme.PUB_SUB_EVENTS) {
        subscribe(window.theme.PUB_SUB_EVENTS.variantChange, (data) => {
          this.handleVariantChange(data);
        });
      }
    }

    handleVariantChange(eventData) {
      if (!eventData || !eventData.data || !eventData.data.variant) {
        return;
      }

      // Refresh product state data from the latest server-rendered variant HTML
      this.updateProductStateDataFromVariantHtml(eventData.data.html);

      const variant = eventData.data.variant;
      const isAvailable = variant.available;
      const productState = this.productStateData.productState;
      const isQuantitySoldOut = Boolean(this.productStateData.isQuantitySoldOut);

      // Update button based on availability and product state
      this.updateButton(isAvailable, productState, variant, isQuantitySoldOut);
      this.updateProductMessages();
    }

    updateProductStateDataFromVariantHtml(variantHtmlDocument) {
      if (!variantHtmlDocument) {
        return;
      }

      const stateDataEl = variantHtmlDocument.getElementById('product-state-data');
      if (!stateDataEl) {
        return;
      }

      try {
        this.productStateData = JSON.parse(stateDataEl.textContent);
      } catch (e) {
        console.error('Failed to parse variant product state data:', e);
      }
    }

    updateProductMessages() {
      if (!this.productStateData) {
        return;
      }

      const leadingTimeEl = document.getElementById('leading-time');
      const shippingTimeEl = document.getElementById('shipping-time');

      this.updateMessageElement(leadingTimeEl, this.productStateData.leadingTime);
      this.updateMessageElement(shippingTimeEl, this.productStateData.shippingTime);
    }

    updateMessageElement(element, text) {
      if (!element) {
        return;
      }

      if (text) {
        element.textContent = text;
        this.showMessageBlock(element);
      } else {
        this.hideMessageBlock(element);
      }
    }

    hideMessageBlock(element) {
      const block = this.getHighestProductBlock(element);
      if (!block) {
        return;
      }

      block.style.display = 'none';

      const previousElement = block.previousElementSibling;
      if (previousElement && previousElement.classList.contains('product__block--divider')) {
        previousElement.style.display = 'none';
      }
    }

    showMessageBlock(element) {
      const block = this.getHighestProductBlock(element);
      if (!block) {
        return;
      }

      block.style.display = '';

      const previousElement = block.previousElementSibling;
      if (previousElement && previousElement.classList.contains('product__block--divider')) {
        previousElement.style.display = '';
      }
    }

    getHighestProductBlock(element) {
      let parentBlock = null;
      let currentElement = element;

      while (currentElement.parentElement) {
        currentElement = currentElement.parentElement;
        if (currentElement.classList.contains('product__block')) {
          parentBlock = currentElement;
        }
      }

      return parentBlock;
    }

    updateButton(isAvailable, productState, variant, isQuantitySoldOut = false) {
      if (!this.submitButton || !this.submitButtonText) {
        this.findSubmitButton();
        if (!this.submitButton || !this.submitButtonText) {
          return;
        }
      }

      let buttonText = '';
      let zohoFormSnippet = '';

      if (productState === 'made-to-order' && isQuantitySoldOut) {
        buttonText = 'Add to Cart';
        if (isAvailable) {
          this.submitButton.removeAttribute('data-zoho-form-snippet');
        } else {
          this.submitButton.setAttribute('data-zoho-form-snippet', 'zoho-connect-with-serpentine');
          this.submitButton.setAttribute('data-product-title', this.productStateData.productTitle || document.title);
        }
        this.submitButton.removeAttribute('disabled');
      } else if (!isAvailable) {
        // Check if variant is available
        // Variant is sold out - apply product state logic and set form snippet
        // Button should remain ENABLED to trigger slideout

        switch (productState) {
          case 'made-to-order':
            buttonText = 'Inquire About Availability';
            zohoFormSnippet = 'zoho-connect-with-serpentine';
            break;
          case 'one-off':
            buttonText = 'Customize This Piece';
            zohoFormSnippet = 'zoho-begin-your-project';
            break;
          case 'one-kind-vintage':
            buttonText = 'Source Something Similar';
            zohoFormSnippet = 'zoho-vintage-request-form';
            break;
          case 'request':
            buttonText = 'Inquire';
            zohoFormSnippet = 'zoho-connect-with-serpentine';
            break;
          case 'customizable':
            buttonText = 'Inquire About Availability';
            zohoFormSnippet = 'zoho-connect-with-serpentine';
            break;
          default:
            buttonText = window.theme.strings.soldOut || 'Sold out';
            // No form snippet for default soldout
        }

        // Add zoho form snippet attribute to trigger slideout
        if (zohoFormSnippet) {
          this.submitButton.setAttribute('data-zoho-form-snippet', zohoFormSnippet);
          this.submitButton.setAttribute('data-product-title', this.productStateData.productTitle || document.title);
        }
        
        // Keep button enabled so it can trigger slideout
        this.submitButton.removeAttribute('disabled');
        
      } else {
        // Variant is available - normal add to cart behavior
        
        // Remove zoho form snippet if present
        this.submitButton.removeAttribute('data-zoho-form-snippet');
        
        // Ensure button is enabled
        this.submitButton.removeAttribute('disabled');

        // Check for preorder
        const isPreorder = this.submitButton.dataset.preorder === 'true' || variant.preorder;
        
        if (isPreorder) {
          buttonText = window.theme.strings.preOrder || 'Pre-order';
        } else {
          buttonText = window.theme.strings.addToCart || 'Add to cart';
        }
      }

      // Update button text (preserve product price if present)
      const priceElement = this.submitButtonText.querySelector('[data-product-price]');
      if (priceElement) {
        const priceHTML = priceElement.outerHTML;
        this.submitButtonText.textContent = buttonText;
        this.submitButtonText.insertAdjacentHTML('beforeend', ` ${priceHTML}`);
      } else {
        this.submitButtonText.textContent = buttonText;
      }
    }
  }

  // Initialize when script loads
  new ProductStateHandler();
})();
